import { ManagerNavigation } from './ManagerNavigation';

const STEPS = [
  {
    number: '1',
    title: '選擇 Google 帳號並確認銷售地點',
    body: '第一次使用請選擇自己的 Google 帳號；名單內的人員會自動帶入並鎖定姓名。若手機登入多個帳號，可按「切換帳號」重新選擇。一般回報的日期與客到時間由送出當下自動記錄；只有補填才可修改，且必須填寫原因。'
  },
  {
    number: '2',
    title: '選擇成交或未成交',
    body: '一位來客只填一筆。成交時填寫商品型號與數量；未成交時改填原因、競品與對談摘要。若未成交原因選「其他」，必須再填寫原因內容。'
  },
  {
    number: '3',
    title: '用型號搜尋快速選取',
    body: '輸入型號任意四碼、完整型號或常用別名，再點選候選項目。必須從候選清單選取，避免不同寫法造成統計錯誤。'
  },
  {
    number: '4',
    title: '取得定位並再次核對身分',
    body: '完成資料後按「送出定位及資料」。系統會在每一筆送出時重新核對目前 Google 帳號與啟用人員名單，不會只採用手機先前記住的姓名。'
  },
  {
    number: '5',
    title: '看到「回報完成」才離開',
    body: '成交會顯示本次成交台數；未成交只顯示完成。'
  }
] as const;

export function Manual() {
  const reportHref = new URLSearchParams(window.location.search).get('test') === '1'
    ? '/?test=1&openExternalBrowser=1'
    : '/?openExternalBrowser=1';
  return (
    <>
      <ManagerNavigation pageTitle="銷售操作說明" currentPage="sales-manual" />
      <main className="manual-shell">
      <header className="manual-hero">
        <a className="manual-back" href={reportHref}>← 返回回報表</a>
        <p className="eyebrow">MNT STORE SALES</p>
        <h1>駐點銷售回報操作說明</h1>
        <p>門市駐點與活動支援都以自己的帳號回報；每位來客只填一次。</p>
      </header>

      <section className="manual-section" aria-labelledby="manual-steps-title">
        <div className="manual-heading">
          <span>快速流程</span>
          <h2 id="manual-steps-title">五個步驟完成一筆回報</h2>
        </div>
        <ol className="manual-steps">
          {STEPS.map(step => (
            <li key={step.number}>
              <span className="manual-step-number" aria-hidden="true">{step.number}</span>
              <div><h3>{step.title}</h3><p>{step.body}</p></div>
            </li>
          ))}
        </ol>
      </section>

      <section className="manual-section" aria-labelledby="manual-identity-title">
        <div className="manual-heading">
          <span>帳號與名單</span>
          <h2 id="manual-identity-title">每一筆都以目前 Google 帳號核對</h2>
        </div>
        <ul className="manual-bullets">
          <li>請使用自己的 Gmail 或公司 Google 帳號，不要代替其他同仁送出。</li>
          <li>正式名單內的人員會直接顯示姓名；帳號被停用後，新的回報會立即停止。</li>
          <li>尚未加入正式名單的人員會顯示「臨時」，必須填寫實際姓名；請盡快通知管理人員補入名單。</li>
          <li>切換 Google 帳號不會清除尚未送出的草稿；草稿可編輯時才可切換。</li>
          <li>受控測試網址固定使用 test，資料與正式統計隔離。</li>
        </ul>
      </section>

      <section className="manual-section" aria-labelledby="manual-activity-title">
        <div className="manual-heading">
          <span>展覽與短期支援</span>
          <h2 id="manual-activity-title">銷售地點選「活動支援」</h2>
        </div>
        <ul className="manual-bullets">
          <li>照常使用自己的 Google 帳號與姓名，原駐點設定保留。</li>
          <li>不需預先建立活動名稱、日期或場地；選「活動支援」就能填報。</li>
          <li>送出時記錄當下 GPS，不以事先設定的會場座標判定距離；仍須允許手機定位。</li>
          <li>依人員與日期統計。較早日期先勾補填並填寫原因，不能提前送出未來回報。</li>
          <li>各自記錄本人接待的來客與成交，同一位來客只由一位負責人員填一次。</li>
          <li>LINE 仍由每人各自傳文字指令：「到點/活動支援/初始台數」及「下班/活動支援/總台數/三星台數」，台數位置換成實際數字。</li>
          <li>LINE 台數填當天支援現場的累計數，不重複加上同事已回報的累計數。</li>
        </ul>
      </section>

      <section className="manual-section" aria-labelledby="manual-sale-title">
        <div className="manual-heading">
          <span>成交範例</span>
          <h2 id="manual-sale-title">同一位客人購買多款商品</h2>
        </div>
        <div className="manual-example">
          <div><strong>品項 1</strong><span>A 款 × 3 台</span></div>
          <div><strong>品項 2</strong><span>B 款 × 1 台</span></div>
          <p>只填一筆來客，按「新增另一款」加入第二個品項。完成後統計為來客 1、成交 4 台。</p>
        </div>
      </section>

      <section className="manual-section" aria-labelledby="manual-recovery-title">
        <div className="manual-heading">
          <span>草稿與斷線</span>
          <h2 id="manual-recovery-title">尚未完成時不要重填</h2>
        </div>
        <ul className="manual-bullets">
          <li>輸入內容會自動保存在目前裝置。</li>
          <li>切換 Google 帳號不會清除尚未送出的草稿。</li>
          <li>如果送出時斷線，保留頁面；連線恢復後按畫面上的確認按鈕。</li>
          <li>只有看到「回報完成」後，系統才會清除這筆草稿。</li>
          <li>要放棄未送內容時，按「清除草稿，開始新回報」；舊草稿會立即刪除，僅保留人員與門市預設。</li>
          <li>每支手機只有一份草稿，請勿同時開多個分頁填寫。</li>
        </ul>
      </section>

      <section className="manual-section manual-boundary" aria-labelledby="manual-boundary-title">
        <div className="manual-heading">
          <span>流程分工</span>
          <h2 id="manual-boundary-title">LINE 群組仍照原流程</h2>
        </div>
        <p>到點、下班、初始台數、總台數、Samsung 台數及群組照片仍在 LINE 群組回報。這個網頁處理門市與活動支援的個人來客、成交與未成交紀錄，不會把內容推送到公共群組。</p>
      </section>

        <a className="manual-primary" href={reportHref}>開始填寫銷售回報</a>
      </main>
    </>
  );
}
